//============================================================================
// Name        : sidebar.ts
// Purpose     : Implement inventory page sidebar logic
// Created     : 2020-01-02
// Author      : Senitron Team
// Copyright   : Copyright 2020 Senitron Corporation. All rights reserved.
//============================================================================

import axios from "axios";
import * as QueryString from "query-string";
import {ICustomNodeOptions, SeTreeview} from "../controls/se_treeview";
import {InitPage} from "../index";

declare var storeLocation: string;

declare var inventoryUrl: string;
declare var loadCategoriesUrl: string;
declare var loadLastSeenUrl: string;
declare var inventoryStatsUrl: string;
declare var inventoryCountZones: string;
declare var inventoryCountStatus: string;
declare var inventoryCountBrand: string;
declare var inventoryCountVendor: string;
declare var inventoryCountCategories: string;
declare var inventoryCountItemTypes: string;

declare var inventoryFetchZones: string;
declare var inventoryFetchSubZones: string;
declare var inventoryFetchStatus: string;
declare var inventoryFetchBrand: string;
declare var inventoryFetchVendor: string;
declare var inventoryFetchItemTypes: string;
declare var inventoryFetchItemsOut: string;

declare var paramsObj: object;

// Global variable to track subzone loading state
let isSubzoneLoading = false;

interface IZoneStat {
    id: any;
    count: number;
}

interface IStatusStat {
    id: number;
    count: number;
}

interface IBrandStat {
    id: string;
    count: number;
}

interface IVendorStat {
    id: string;
    count: number;
}

interface IItemTypeStat {
    id: number;
    count: number;
}

interface IInventoryStats {
    total_count: number;
    item_count: number;
    filtered_total_count: number;
    filtered_item_count: number;
    zones: IZoneStat[];
    status: IStatusStat[];
    brand: IBrandStat[];
    vendor: IVendorStat[];
    item_types: IItemTypeStat[];
}

export function LoadPage() {
    InitPage("epc_tags inventory_logs", () => {
        const treeview = new SeTreeview();
        treeview.setListCounter("zones", async () => {
            // Set flag to skip subzone updates during count operations
            (window as any).skipSubzoneUpdates = true;
            
            if(!($('#rfid_zone_filter').prop('checked') || $('#static_zone_filter').prop('checked'))) {
              // Reset only zone counts, exclude subzones by targeting zone_filter[] checkboxes
              $(".zones-all-list").find('input[name="zone_filter[]"]').closest('label').next('label[data-record-id]').find('div').html(` (0)`);
              // Also reset zone-level counts but preserve subzone counts
              $(".zones-list").find('input[name="zone_filter[]"]').closest('label').next('label[data-record-id]').find('div').html(` (0)`);
              $(`.zones-spinner`).hide();
              // Clear flag after count operation
              (window as any).skipSubzoneUpdates = false;
              return;
            }
            // Get the url from the parent
            const resp = await axios.get(inventoryCountZones, {
                params: {
                    rfid_zone_filter: $('#rfid_zone_filter').is(':checked'),
                    static_zone_filter: $('#static_zone_filter').is(':checked')
                }
            });
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }
            // Init all with zero (but exclude subzones)
            $(".zones-all-list").find('input[name="zone_filter[]"]').closest('label').next('label[data-record-id]').find('div').html(` (0)`);
            // Also reset zone-level counts but preserve subzone counts
            $(".zones-list").find('input[name="zone_filter[]"]').closest('label').next('label[data-record-id]').find('div').html(` (0)`);
            // Update zone counts - now zones are returned at zone level, not subzone level
            const zonesList = $(".zones-list ul");
            for (const z of resp.data.zones) {
                if (z.id == "unassigned") {
                    const rootel = $(".zones-list").find(`[data-record-id='Un Assigned']`).find("div");
                    rootel.html(` (${z.count})`);
                    rootel.parent().data("count", z.count);
                    continue;
                }

                // Update zone-level count directly (exclude subzones by targeting zone_filter[] checkboxes)
                const el = $(".zones-list").find(`input[name="zone_filter[]"][value="${z.id}"]`).closest('label').next('label[data-record-id]').find("div");
                el.html(` (${z.count})`);
                el.parent().data("count", z.count);
            }
            const nodeZoneAll = $(".zones-list").find(`[data-record-id='all']`).find("div");
            nodeZoneAll.html('');
            
            // Update subzone counts for all zones that have subzones loaded
            await updateLoadedSubzoneCounts();
            
            treeview.countFinished("zones");
            
            // Clear flag after count operation
            (window as any).skipSubzoneUpdates = false;
        });
        treeview.setListCounter("status", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountStatus);
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            // Update status counts
            for (const s of resp.data.status) {
                const el = $(".status-list").find(`[data-record-id='${s.id}']`).find("div");
                el.html(` (${s.count})`);
                el.parent().data("count", s.count);
            }
            treeview.countFinished("status");
        });
        treeview.setListCounter("brands", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountBrand);
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            // Update brand counts
            for (const s of resp.data.brand) {
                const el = $(".brands-list").find(`[data-record-id='${treeview.safeHtml(s.id)}']`).find("div");
                el.html(` (${s.count})`);
                el.parent().data("count", s.count);
            }
            treeview.countFinished("brands");
        });
        treeview.setListCounter("vendors", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountVendor);
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            // Update vendor counts
            for (const s of resp.data.vendor) {
                const el = $(".vendors-list").find(`[data-record-id='${treeview.safeHtml(s.id)}']`).find("div");
                el.html(` (${s.count})`);
                el.parent().data("count", s.count);
            }
            treeview.countFinished("vendors");
        });
        treeview.setListCounter("categories", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountCategories + ".json");
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            const updateCounterFunc = (cats) => {
                // Update category counts
                for (const cat of cats) {
                    const el = $(".categories-list").find(`[data-record-id='${cat.id}']`).find("div");
                    el.html(` (${cat.count})`);
                    el.parent().data("count", cat.count);

                    updateCounterFunc(cat.children);
                }
                treeview.countFinished("categories");
            };
            updateCounterFunc(resp.data);
        });
        treeview.setListCounter("type", async () => {
            // Get the url from the parent
            const resp = await axios.get(inventoryCountItemTypes);
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            // Update item type counts
            for (const t of resp.data.itemTypes) {
                const el = $(".type-list").find(`[data-record-id='${t.id}']`).find("div");
                el.html(` (${t.count})`);
                el.parent().data("count", t.count);
            }
            treeview.countFinished("type");
        });

        // Load and count zones
        const loadAssetCountFunc = async () => {
            (paramsObj as any).stats_count = true;
            const resp = await axios.get(inventoryStatsUrl, { params: paramsObj });
            if (resp.status != 200) {
                return window.showErrorDialog("Error getting inventory stats");
            }

            const stats: IInventoryStats = resp.data;

            // Update total asset count
            $("#totalAssetInventoryCount").text(stats.total_count);
            $("#totalItemInventoryCount").text(stats.item_count);

            $('#auditRequestSkuCount').text(stats.filtered_item_count);
            $('#auditRequestAssetCount').text(stats.filtered_total_count);
        };

        // Async load all the sidebar data on a delay
        setTimeout(
            () => {
                loadAssetCountFunc().then();

                treeview.loadNodes("zones", inventoryFetchZones, {
                    altNodeName: "zone",
                    childrenNodeName: "sub_zone"
                } as ICustomNodeOptions).then(() => {
                    // Set up lazy loading for zones
                    setupZoneLazyLoading();
                    treeview.setCheckedNodes("zones", {
                        altNodeName: "zone",
                        childrenNodeName: "sub_zone"
                    } as ICustomNodeOptions);
                    
                    // Ensure all zone expansion checkboxes are unchecked to show ▶ arrow
                    // This includes both individual zones and the "All" node
                    $('.zones-list input[type="checkbox"][id]').prop('checked', false);
                    
                    // Also ensure any zone expansion checkboxes that might have been checked by setCheckedNodes are unchecked
                    $('.zones-list li input[type="checkbox"][id]').prop('checked', false);
                    
                    // Set up zone search functionality
                    setupZoneSearch();
                    
                    // Expand zones based on selectedZonesIds from backend
                    // This is much more efficient as the backend already knows which zones to expand
                    setTimeout(() => {
                        expandZonesFromBackendData();
                    }, 100);
                });

                treeview.loadNodes("categories", loadCategoriesUrl + ".json")
                        .then(() => treeview.setCheckedNodes("categories"));

                treeview.loadNodes("status", inventoryFetchStatus)
                        .then(() => treeview.setCheckedNodes("status"));

                treeview.loadNodes("brands", inventoryFetchBrand)
                    .then(() => treeview.setCheckedNodes("brands"));

                treeview.loadNodes("vendors", inventoryFetchVendor)
                    .then(() => treeview.setCheckedNodes("vendors"));

                treeview.loadNodes("last_seen", loadLastSeenUrl)
                        .then(() => treeview.setCheckedNodes("last_seen"));

                treeview.loadNodes("type", inventoryFetchItemTypes, {
                    altNodeName: "sort_item",
                    childrenNodeName: "sort_item"
                } as ICustomNodeOptions).then(() => treeview.setCheckedNodes("type", {
                    altNodeName: "sort_item"
                } as ICustomNodeOptions));

                treeview.loadNodes("items_out", inventoryFetchItemsOut)
                        .then(() => treeview.setCheckedNodes("items_out"));
            },
            1000 // Initial delay before populating remaining sections
        );

        // On page load keep track of the filtering options
        const currentFilters  = QueryString.parse(location.search);
        delete currentFilters.utf8;
        let savedFiltersStr = localStorage.getItem("inventoryFilters");

        // Check if we've switched locations, in that case invalidate the filters
        // to prevent issues, on load.
        if (savedFiltersStr) {
            const savedFilters = QueryString.parse(savedFiltersStr);
            if (savedFilters.location != storeLocation) {
                localStorage.removeItem("inventoryFilters");
                location.href = inventoryUrl;
                return;
            }
        }

        // Should restore filters
        if (Object.keys(currentFilters).length <= 1 && savedFiltersStr) {
            const savedFilters = QueryString.parse(savedFiltersStr);
            if (savedFilters.utf8) {
                delete savedFilters.utf8;
            }
            if (!currentFilters.search) {
                if (savedFilters.search) {
                    delete savedFilters.search;
                }
                if (Object.keys(currentFilters).length == Object.keys(savedFilters).length) {
                    return;
                }
            }
            else {
                if (!location.search.includes("filter")) {
                    savedFilters.search = currentFilters.search;
                    savedFilters.multilocation = currentFilters.multilocation;
                    savedFilters.location = storeLocation;
                    savedFiltersStr = QueryString.stringify(savedFilters);
                }
            }
            if (!location.search.includes("antenna_id")) {
                location.search = savedFiltersStr;
            }
        }

        // Should save new filters
        delete currentFilters.search;
        delete currentFilters.multilocation;
        if (Object.keys(currentFilters).length > 1) {
            localStorage.setItem("inventoryFilters", location.search + `&location=${storeLocation}`);
        }
    });

    InitPage("epc_tags", () => {
        // Handle clearing inventory filters
        $(".clear-filters").on("click", () => {
            console.log("Clearing filters");
            localStorage.removeItem("inventoryFilters");
            location.href = inventoryUrl;
        });
        window.onbeforeunload =  (e) => {
            window.onunload = () => {
                window.localStorage.removeItem("inventoryFilters");
            }
            return undefined;
        };
    });
}

// Function to update subzone counts for all zones that have subzones loaded
async function updateLoadedSubzoneCounts() {
    // Find all zones that have subzones loaded (have a ul with subzone items)
    const zonesWithSubzones: JQuery[] = [];
    $('.zones-list li').each(function() {
        const $ul = $(this).find('ul');
        if ($ul.length > 0 && $ul.find('.subzone-item').length > 0) {
            zonesWithSubzones.push($(this));
        }
    });
    
    // Update subzone counts for each zone
    const updatePromises = zonesWithSubzones.map(async ($zoneItem) => {
        const $zoneLabel = $zoneItem.find('label[data-record-id]');
        const zoneId = $zoneLabel.data('record-id');
        
        // Skip "all" and "unassigned" zones
        if (!zoneId || zoneId === 'all' || zoneId === 'Un Assigned') {
            return;
        }
        
        try {
            // Fetch updated subzone counts
            const resp = await axios.get(inventoryFetchSubZones, {
                params: { 
                    zone_id: zoneId,
                    rfid_zone_filter: $('#rfid_zone_filter').is(':checked'),
                    static_zone_filter: $('#static_zone_filter').is(':checked')
                }
            });
            
            if (resp.status === 200) {
                if (resp.data.length > 0) {
                    // Update each subzone count
                    resp.data.forEach((subZone: any) => {
                        const $subZoneLabel = $zoneItem.find(`label[data-record-id="${subZone.id}"]`);
                        if ($subZoneLabel.length > 0) {
                            const $countDiv = $subZoneLabel.find('div');
                            $countDiv.html(`(${subZone.count})`);
                        }
                    });
                } else {
                    // API returned empty result - set all subzone counts to 0
                    $zoneItem.find('.subzone-item label[data-record-id]').each(function() {
                        const $countDiv = $(this).find('div');
                        $countDiv.html(`(0)`);
                    });
                }
            }
        } catch (error) {
            console.error(`Error updating subzone counts for zone ${zoneId}:`, error);
        }
    });
    
    // Wait for all updates to complete
    await Promise.all(updatePromises);
}

function setupZoneLazyLoading() {
    // Handle checkbox changes for zone expansion checkboxes
    // This handles both direct checkbox clicks and programmatic changes
    $('.zones-list').on('change', 'input[type="checkbox"][id]:not([name])', function(e) {
        const $checkbox = $(this);
        const $zoneItem = $checkbox.closest('li');
        const $subZoneList = $zoneItem.find('ul');
        const zoneId = $zoneItem.find('label[data-record-id]').data('record-id');
        
        // Skip the "All" node - it has its own handler
        if (zoneId === 'all') {
            return;
        }
        
        // Only handle if subzones are already loaded
        if ($subZoneList.length > 0 && $subZoneList.children().length > 0) {
            const isChecked = $checkbox.is(':checked');
            
            // Properly toggle the classes based on checkbox state
            // The CSS will automatically update the arrow (▶/▼) based on checkbox state
            if (isChecked) {
                $subZoneList.removeClass('slideup').addClass('slidedown');
            } else {
                $subZoneList.removeClass('slidedown').addClass('slideup');
            }
        }
    });
    
    // Add specific handler for "All" node to prevent individual zone expansion checkboxes from being checked
    $('.zones-list').on('click', 'label[for][data-record-id="all"]', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Allow the normal expansion behavior for the "All" node
        const $label = $(this);
        const $zoneItem = $label.closest('li');
        const $checkbox = $zoneItem.find('input[type="checkbox"][id]');
        const $subZoneList = $zoneItem.find('ul');
        
        // Check checkbox state to determine current state (more reliable than class state)
        const isCurrentlyChecked = $checkbox.is(':checked');
        const newCheckedState = !isCurrentlyChecked;
        $checkbox.prop('checked', newCheckedState);
        
        // Properly toggle the classes based on new checkbox state
        // The CSS will automatically update the arrow (▶/▼) based on checkbox state
        if (newCheckedState) {
            $subZoneList.removeClass('slideup').addClass('slidedown');
        } else {
            $subZoneList.removeClass('slidedown').addClass('slideup');
        }
        
        // Ensure individual zone expansion checkboxes remain unchecked
        setTimeout(() => {
            $('.zones-all-list li input[type="checkbox"][id]').prop('checked', false);
        }, 0);
    });
    
    // Add click handlers for zone expansion using the CSS-based ▶ icon
    $('.zones-list').on('click', 'label[for]', async function(e) {
        const $label = $(this);
        const $zoneItem = $label.closest('li');
        const zoneId = $label.data('record-id');
        
        // Skip if this is a subzone item (don't trigger zone expansion when clicking subzones)
        if ($zoneItem.hasClass('subzone-item') || $zoneItem.find('input[name="sub_zone_filter[]"]').length > 0) {
            return;
        }
        
        // Skip the "All" node since it has its own handler
        if (zoneId === 'all') {
            return;
        }
        
        // Find checkbox using the label's 'for' attribute (more reliable)
        const labelFor = $label.attr('for');
        const $checkbox = labelFor ? $(`#${labelFor}`) : $zoneItem.find('input[type="checkbox"][id]:not([name])');
        
        if ($checkbox.length === 0) {
            console.warn('Zone expansion checkbox not found for zone:', zoneId);
            return;
        }
        
        // Check if this is a zone that should have sub-zones loaded
        const $subZoneList = $zoneItem.find('ul');
        if ($subZoneList.length > 0 && $subZoneList.children().length > 0) {
            // Already loaded, just toggle visibility using the existing checkbox system
            e.preventDefault();
            e.stopPropagation();
            
            // Check checkbox state to determine current state (more reliable than class state)
            const isCurrentlyChecked = $checkbox.is(':checked');
            const newCheckedState = !isCurrentlyChecked;
            
            // Toggle checkbox state - use both prop and attr to ensure it's set
            $checkbox.prop('checked', newCheckedState);
            $checkbox.attr('checked', newCheckedState ? 'checked' : null);
            
            // Properly toggle the classes based on new checkbox state
            // The CSS will automatically update the arrow (▶/▼) based on checkbox state
            if (newCheckedState) {
                $subZoneList.removeClass('slideup').addClass('slidedown');
            } else {
                $subZoneList.removeClass('slidedown').addClass('slideup');
            }
            
            // Force a reflow to ensure CSS updates
            if ($checkbox[0] instanceof HTMLElement) {
                void $checkbox[0].offsetHeight;
            }
            
            return;
        }
        
        // For zones without loaded subzones, prevent default to handle async loading
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent multiple API calls using global loading variable
        if (isSubzoneLoading) {
            console.log('Subzone API already loading, ignoring click for zone:', zoneId);
            return;
        }
        
        // Set global loading state to true
        isSubzoneLoading = true;
        
        // Show loading indicator by temporarily disabling the checkbox
        $checkbox.prop('disabled', true);
        const $spinner = $('<i class="fa fa-spinner fa-spin" style="margin-left: 5px; color: #666;"></i>');
        $label.append($spinner);
        
        try {
            // Fetch sub-zones for this zone
            const resp = await axios.get(inventoryFetchSubZones, {
                params: { 
                    zone_id: zoneId,
                    rfid_zone_filter: $('#rfid_zone_filter').is(':checked'),
                    static_zone_filter: $('#static_zone_filter').is(':checked')
                }
            });
            
            if (resp.status === 200 && resp.data.length > 0) {
                // Skip subzone updates during count operations
                if ((window as any).skipSubzoneUpdates) {
                    $checkbox.prop('checked', true);
                    return;
                }
                
                // Create sub-zone list if it doesn't exist
                let $targetSubZoneList = $subZoneList;
                if ($subZoneList.length === 0) {
                    $zoneItem.append('<ul class="slideup"></ul>');
                    $targetSubZoneList = $zoneItem.find('ul');
                }
                
                // Add sub-zones to the list (no arrows for sub-zones)
                const subZoneHtml = `
                    <div class="subzone-search-container" style="margin: 2px 2px; border-bottom: 1px solid #ddd; background: #f8f9fa;">
                        <input type="text" class="subzone-search form-control input-sm" placeholder="Search sub-zones..." style="font-size: 10px; height: 20px;">
                    </div>
                    ${resp.data.map(subZone => `
                        <li class="subzone-item">
                            <input type="checkbox" id="sub_zone_${subZone.id}" class="selection-node" />
                            <label>
                                <input type="checkbox" name="sub_zone_filter[]" value="${subZone.id}" />
                                <span class="check-target"></span>
                            </label>
                            <label class="seTreeview-bare-label" for="sub_zone_${subZone.id}" data-record-id="${subZone.id}">
                                ${subZone.name}<div>(${subZone.count})</div>
                            </label>
                        </li>
                    `).join('')}`;
                
                $targetSubZoneList.html(subZoneHtml);
                
                // Expand the zone treeview by removing slideup and adding slidedown classes
                $targetSubZoneList.removeClass('slideup').addClass('slidedown');
                $checkbox.prop('checked', true);
                
                // Set up sub-zone search functionality
                setupSubZoneSearch($targetSubZoneList);
                
                // Set up event handlers for subzone selection to auto-select parent zone
                setupSubZoneParentSelection($targetSubZoneList);
            } else {
                // No sub-zones found, keep checkbox unchecked
                $checkbox.prop('checked', false);
            }
        } catch (error) {
            console.error('Error loading sub-zones:', error);
            $checkbox.prop('checked', false);
        } finally {
            // Remove spinner and re-enable the checkbox
            $spinner.remove();
            $checkbox.prop('disabled', false);
            // Reset global loading state
            isSubzoneLoading = false;
        }
    });
}

function setupZoneSearch() {
    const $searchInput = $('#zone-search');
    const $zonesList = $('.zones-list');
    
    // Store original zone data for filtering
    let originalZonesData: any[] = [];
    
    // Function to filter zones based on search term
    function filterZones(searchTerm: string) {
        const term = searchTerm.toLowerCase().trim();
        
        if (term === '') {
            // Show all zones
            $zonesList.find('li').show();
            return;
        }
        
        // Hide all zones first
        $zonesList.find('li').hide();
        
        // Show zones that match the search term
        $zonesList.find('li').each(function() {
            const $li = $(this);
            const zoneName = $li.find('label[data-record-id]').text().toLowerCase();
            
            if (zoneName.includes(term)) {
                $li.show();
                
                // If this zone has sub-zones loaded, also show them
                const $subZones = $li.find('ul li');
                $subZones.show();
            }
        });
        
        // Always show the "All" node
        $zonesList.find('li:first').show();
    }
    
    // Set up search input event handler
    $searchInput.on('input', function() {
        const searchTerm = $(this).val() as string;
        filterZones(searchTerm);
    });
    
    // Clear search when escape key is pressed
    $searchInput.on('keydown', function(e) {
        if (e.key === 'Escape') {
            $(this).val('');
            filterZones('');
        }
    });
    
    // Add clear button functionality
    $searchInput.on('focus', function() {
        if ($(this).val() === '') {
            return;
        }
        
        // Add clear button if not already present
        if ($(this).siblings('.search-clear').length === 0) {
            $(this).after('<span class="search-clear" style="position: absolute; right: 25px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #999;">×</span>');
        }
    });
    
    // Handle clear button click
    $('.zone-search-container').on('click', '.search-clear', function() {
        $searchInput.val('');
        filterZones('');
        $(this).remove();
    });
}

function setupSubZoneSearch($subZoneList: JQuery) {
    const $searchInput = $subZoneList.find('.subzone-search');
    
    // Function to filter sub-zones based on search term
    function filterSubZones(searchTerm: string) {
        const term = searchTerm.toLowerCase().trim();
        
        if (term === '') {
            // Show all sub-zones
            $subZoneList.find('.subzone-item').show();
            return;
        }
        
        // Hide all sub-zones first
        $subZoneList.find('.subzone-item').hide();
        
        // Show sub-zones that match the search term
        $subZoneList.find('.subzone-item').each(function() {
            const $li = $(this);
            const subZoneName = $li.find('label[data-record-id]').text().toLowerCase();
            
            if (subZoneName.includes(term)) {
                $li.show();
            }
        });
    }
    
    // Set up search input event handler
    $searchInput.on('input', function() {
        const searchTerm = $(this).val() as string;
        filterSubZones(searchTerm);
    });
    
    // Clear search when escape key is pressed
    $searchInput.on('keydown', function(e) {
        if (e.key === 'Escape') {
            $(this).val('');
            filterSubZones('');
        }
    });
    
    // Add clear button functionality
    $searchInput.on('focus', function() {
        if ($(this).val() === '') {
            return;
        }
        
        // Add clear button if not already present
        if ($(this).siblings('.search-clear').length === 0) {
            $(this).after('<span class="search-clear" style="position: absolute; right: 25px; top: 50%; transform: translateY(-50%); cursor: pointer; color: #999;">×</span>');
        }
    });
    
    // Handle clear button click
    $subZoneList.on('click', '.search-clear', function() {
        $searchInput.val('');
        filterSubZones('');
        $(this).remove();
    });
}

function setupSubZoneParentSelection($subZoneList: JQuery) {
    // Set up event handlers for subzone checkboxes to auto-select parent zone
    $subZoneList.on('change', 'input[name="sub_zone_filter[]"]', function() {
        const $subzoneCheckbox = $(this);
        
        // When a subzone is selected, also select its parent zone
        if ($subzoneCheckbox.is(':checked')) {
            // Find the parent zone checkbox (zone_filter[])
            const $parentZoneItem = $subzoneCheckbox.closest('ul').closest('li');
            const $parentZoneCheckbox = $parentZoneItem.find('input[name="zone_filter[]"]');
            
            if ($parentZoneCheckbox.length > 0) {
                $parentZoneCheckbox.prop('checked', true);
            }
        }
    });
}

// Function to load subzones and check all of them when a zone is checked
(window as any).loadSubzonesAndCheckAll = async function(zoneId: string, $zoneItem: JQuery, $zoneLabel: JQuery) {
    const $checkbox = $zoneItem.find('input[type="checkbox"][id]');
    const $subZoneList = $zoneItem.find('ul');
    
    // Load sub-zones if not already loaded
    if ($subZoneList.length === 0 || $subZoneList.children().length === 0) {
        // Prevent multiple API calls using global loading variable
        if (isSubzoneLoading) {
            console.log('Subzone API already loading, ignoring loadSubzonesAndCheckAll for zone:', zoneId);
            return;
        }
        
        // Set global loading state to true
        isSubzoneLoading = true;
        
        // Show loading indicator
        $checkbox.prop('disabled', true);
        const $spinner = $('<i class="fa fa-spinner fa-spin" style="margin-left: 5px; color: #666;"></i>');
        $zoneLabel.append($spinner);
        
        try {
            const resp = await axios.get(inventoryFetchSubZones, {
                params: { 
                    zone_id: zoneId,
                    rfid_zone_filter: $('#rfid_zone_filter').is(':checked'),
                    static_zone_filter: $('#static_zone_filter').is(':checked')
                }
            });
            
            if (resp.status === 200 && resp.data.length > 0) {
                // Create sub-zone list if it doesn't exist
                let $targetSubZoneList = $subZoneList;
                if ($subZoneList.length === 0) {
                    $zoneItem.append('<ul class="slideup"></ul>');
                    $targetSubZoneList = $zoneItem.find('ul');
                }
                
                // Add sub-zones to the list with all checked
                const subZoneHtml = `
                    <div class="subzone-search-container" style="margin: 2px 2px; border-bottom: 1px solid #ddd; background: #f8f9fa;">
                        <input type="text" class="subzone-search form-control input-sm" placeholder="Search sub-zones..." style="font-size: 10px; height: 20px;">
                    </div>
                    ${resp.data.map(subZone => `
                        <li class="subzone-item">
                            <input type="checkbox" id="sub_zone_${subZone.id}" class="selection-node" />
                            <label>
                                <input type="checkbox" name="sub_zone_filter[]" value="${subZone.id}" checked />
                                <span class="check-target"></span>
                            </label>
                            <label class="seTreeview-bare-label" for="sub_zone_${subZone.id}" data-record-id="${subZone.id}">
                                ${subZone.name}<div>(${subZone.count})</div>
                            </label>
                        </li>
                    `).join('')}`;
                
                $targetSubZoneList.html(subZoneHtml);
                
                // Expand the zone treeview by removing slideup and adding slidedown classes
                $targetSubZoneList.removeClass('slideup').addClass('slidedown');
                $checkbox.prop('checked', true);
                
                // Set up sub-zone search functionality
                setupSubZoneSearch($targetSubZoneList);
                
                // Set up event handlers for subzone selection to auto-select parent zone
                setupSubZoneParentSelection($targetSubZoneList);
            } else {
                // No sub-zones found, keep checkbox unchecked
                $checkbox.prop('checked', false);
            }
        } catch (error) {
            console.error('Error loading sub-zones:', error);
            $checkbox.prop('checked', false);
        } finally {
            // Remove spinner and re-enable the checkbox
            $spinner.remove();
            $checkbox.prop('disabled', false);
            // Reset global loading state
            isSubzoneLoading = false;
        }
    } else {
        // Subzones already loaded, just check all of them
        $subZoneList.find('input[name="sub_zone_filter[]"]').prop('checked', true);
        // Expand the zone treeview if it was collapsed
        $subZoneList.removeClass('slideup').addClass('slidedown');
        $checkbox.prop('checked', true);
    }
};

// New efficient function that uses backend data to expand only relevant zones
async function expandZonesFromBackendData() {
    // Get the selected zones IDs from the backend
    const selectedZonesIds = (window as any).selectedZonesIds || [];
    
    // Get URL parameters for sub-zone filters to know which sub-zones to check
    const urlParams = new URLSearchParams(window.location.search);
    const subZoneFilters = urlParams.getAll('sub_zone_filter[]');
    const subZoneFilterStr = urlParams.get('sub_zone_filter_str');
    
    let selectedSubZones = [...subZoneFilters];
    if (subZoneFilterStr) {
        selectedSubZones = [...selectedSubZones, ...subZoneFilterStr.split(',')];
    }
    selectedSubZones = Array.from(new Set(selectedSubZones)).filter(z => z && z !== '');
    
    // If no zones or subzones are selected, return early
    if (selectedZonesIds.length === 0 && selectedSubZones.length === 0) {
        return;
    }
    
    // Expand zones that are directly selected
    for (const zoneId of selectedZonesIds) {
        await expandZoneWithBackendData(zoneId, selectedSubZones);
    }
    
    // Also expand zones that contain selected subzones (even if zone itself isn't selected)
    if (selectedSubZones.length > 0) {
        await expandZonesContainingSelectedSubzones(selectedSubZones);
    }
}

// Helper function to expand zones that contain selected subzones
async function expandZonesContainingSelectedSubzones(selectedSubZones: string[]) {
    // Get selected zone IDs from the URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const selectedZonesIds = urlParams.getAll('zone_filter[]');
    
    if (selectedZonesIds.length === 0) {
        console.log('No selected zones found, skipping subzone expansion');
        return;
    }

    const remainingSubZones = new Set(selectedSubZones);
    const expandedZones = new Set<string>();

    // Only process selected zones, not all zones
    for (const zoneId of selectedZonesIds) {
        if (remainingSubZones.size === 0) {
            break; // Stop if we've found all selected subzones
        }

        try {
            const resp = await axios.get(inventoryFetchSubZones, {
                params: { 
                    zone_id: zoneId,
                    rfid_zone_filter: $('#rfid_zone_filter').is(':checked'),
                    static_zone_filter: $('#static_zone_filter').is(':checked')
                }
            });

            if (resp.status === 200 && resp.data.length > 0) {
                const zoneSubZoneIds = resp.data.map(subZone => subZone.id.toString());
                const foundSubZones = selectedSubZones.filter(subZoneId => zoneSubZoneIds.includes(subZoneId));

                if (foundSubZones.length > 0) {
                    expandedZones.add(zoneId);
                    // Remove found subzones from remaining set
                    foundSubZones.forEach(subZoneId => remainingSubZones.delete(subZoneId));

                    // Expand immediately when we find a match
                    await expandZoneWithBackendData(zoneId, selectedSubZones);
                }
            }
        } catch (error) {
            console.error('Error checking subzones for zone:', zoneId, error);
        }
    }

    console.log(`Expanded ${expandedZones.size} zones containing selected subzones`);
}

// Helper function to expand a specific zone using backend data
async function expandZoneWithBackendData(zoneId, selectedSubZones) {
    const $zoneElement = $(`.zones-list [data-record-id="${zoneId}"]`);
    if ($zoneElement.length === 0) {
        return;
    }
    
    const $zoneItem = $zoneElement.closest('li');
    const $checkbox = $zoneItem.find('input[type="checkbox"][id]');
    const $subZoneList = $zoneItem.find('ul');
    
    // Load sub-zones if not already loaded
    if ($subZoneList.length === 0 || $subZoneList.children().length === 0) {
        // Prevent multiple API calls using global loading variable
        if (isSubzoneLoading) {
            console.log('Subzone API already loading, ignoring expandZoneWithBackendData for zone:', zoneId);
            return;
        }
        
        // Set global loading state to true
        isSubzoneLoading = true;
        
        const $spinner = $('<i class="fa fa-spinner fa-spin" style="margin-left: 5px; color: #666;"></i>');
        $zoneElement.append($spinner);
        
        try {
            const resp = await axios.get(inventoryFetchSubZones, {
                params: { 
                    zone_id: zoneId,
                    rfid_zone_filter: $('#rfid_zone_filter').is(':checked'),
                    static_zone_filter: $('#static_zone_filter').is(':checked')
                }
            });
            
            if (resp.status === 200 && resp.data.length > 0) {
                // Skip subzone updates during count operations
                if ((window as any).skipSubzoneUpdates) {
                    return;
                }
                
                // Create sub-zone list if it doesn't exist
                let $targetSubZoneList = $subZoneList;
                if ($subZoneList.length === 0) {
                    $zoneItem.append('<ul class="slideup"></ul>');
                    // Update the reference to the newly created list
                    $targetSubZoneList = $zoneItem.find('ul');
                }
                
                // Show all sub-zones, but we'll check the selected ones
                const subZonesToShow = resp.data;
                
                // Add sub-zones to the list
                const subZoneHtml = `
                    <div class="subzone-search-container" style="margin: 2px 2px; border-bottom: 1px solid #ddd; background: #f8f9fa;">
                        <input type="text" class="subzone-search form-control input-sm" placeholder="Search sub-zones..." style="font-size: 10px; height: 20px;">
                    </div>
                    ${subZonesToShow.map(subZone => `
                        <li class="subzone-item">
                            <input type="checkbox" id="sub_zone_${subZone.id}" class="selection-node" />
                            <label>
                                <input type="checkbox" name="sub_zone_filter[]" value="${subZone.id}" ${selectedSubZones.includes(subZone.id.toString()) ? 'checked' : ''} />
                                <span class="check-target"></span>
                            </label>
                            <label class="seTreeview-bare-label" for="sub_zone_${subZone.id}" data-record-id="${subZone.id}">
                                ${subZone.name}<div>(${subZone.count})</div>
                            </label>
                        </li>
                    `).join('')}`;
                
                $targetSubZoneList.html(subZoneHtml);
                
                // Expand the zone treeview by removing slideup and adding slidedown classes
                $targetSubZoneList.removeClass('slideup').addClass('slidedown');
                $checkbox.prop('checked', true);
                
                // Set up sub-zone search functionality
                setupSubZoneSearch($targetSubZoneList);
                
                // Set up event handlers for subzone selection to auto-select parent zone
                setupSubZoneParentSelection($targetSubZoneList);
            }
        } catch (error) {
            console.error('Error loading sub-zones for zone:', zoneId, error);
        } finally {
            // Remove spinner
            $spinner.remove();
            // Reset global loading state
            isSubzoneLoading = false;
        }
    }
}
